import * as ko from 'knockout';
import * as globalConfig from 'globalConfig';
import * as localisationHelper from 'localisationHelper';
import * as basketSummaryService from 'basketSummaryService';
import * as windowWrapper from 'windowWrapper';
import * as windowHelper from 'windowHelper';
import * as _ from 'underscore';

// Models
import { JoinConfiguration } from 'Content/ts/Models/Join/JoinConfiguration';
import { JoiningStepEnum } from 'Content/ts/Models/Join/JoiningStep';
import { JoinMemberRequestModel } from 'Content/ts/Models/Join/JoinMemberRequestModel';
import { AlertStructure } from "Content/ts/Models/Shared/BootstrapAlerts/alertStructure";
import { IJourneyDetails } from 'Content/ts/Models/Join/Interfaces/IJourneyDetails';
import { PaymentProviderTypes } from 'Content/ts/Enums/UniversalBasket/paymentProviderTypes';

// Components
import 'Content/ts/Join/facility-selection';
import 'Content/ts/Join/membership-selection';
import 'Content/ts/Join/personal-details';
import 'Content/ts/Join/payment';
import 'Content/ts/Join/terms-of-membership';
import "Content/ts/Shared/BootstrapAlerts/AlertDanger/alert-danger";

// Services
import { JoinService } from 'Content/ts/Join/Services/joinService';
import { ValidationService } from 'Content/ts/Shared/Services/Validation/validationService';
import { IMembershipPriceModel } from 'Models/Join/Interfaces/IMembershipPrice';
import { BasketRoutingService } from 'Content/ts/Join/Services/BasketRoutingService';
import { IJoinRequestResponseModel } from 'Content/ts/Models/Join/Interfaces/IJoinRequestResponseModel';
import { FeeProcessType } from 'Content/ts/Models/Shared/Enums/feeProcessType';


export const Deps = {
    GlobalConfig: globalConfig,
    WindowHelper: windowHelper,
    JoinService: JoinService,
};

interface JoinBaseParams {
    JourneyId: number;
    FacilityId: number | null;
    MembershipPriceId: number | null;
    ContactId: number;
}

class JoinBase {
    JoinConfig: JoinConfiguration;
    JoiningStepEnum: ThisType<JoiningStepEnum>;
    ShowMembershipPane: KnockoutObservable<boolean>;
    ShowErrorMessage: KnockoutObservable<boolean>;
    addingToBasket: KnockoutObservable<boolean>;

    nextButtonText: KnockoutComputed<string>;
    nextButtonVisible: KnockoutComputed<boolean>;
    nextButtonEnabled: KnockoutComputed<boolean>;
    currentStepText: KnockoutComputed<string>;
    isFinalStep: KnockoutComputed<boolean>;

    showDirectDebit: KnockoutComputed<boolean>;
    showFacilitySelect: KnockoutComputed<boolean>;
    showMembershipSelect: KnockoutComputed<boolean>;
    showPersonalDetails: KnockoutComputed<boolean>;
    showPayment: KnockoutComputed<boolean>;

    PurchaseErrorMessage: KnockoutObservableArray<AlertStructure>;

    CurrentStepPageTitle: KnockoutComputed<string>;
    ariaCurrent: KnockoutComputed<string>;

    ContactId: number;

    GymSelectionValidationFields : KnockoutObservable<any>[];

    customMessages: {
        NextStep: string;
        PaymentButtonText: string;
        PurchaseError: string;
    };

    constructor(params: JoinBaseParams) {
        this.JoinConfig = new JoinConfiguration(params.JourneyId, params.FacilityId, params.MembershipPriceId, params.ContactId);

        this.JoiningStepEnum = JoiningStepEnum;

        this.ContactId = params.ContactId;

        this.ShowMembershipPane = ko.observable(false);
        this.ShowErrorMessage = ko.observable(false);
        this.addingToBasket = ko.observable(false);

        this.GymSelectionValidationFields = this.JoinConfig.getValidationFields(JoiningStepEnum.GymSelection);

        this.JoinConfig.facilitySelected.add(() => this.ShowMembershipPane(true));
        this.getJourneyDetails(params.JourneyId);
        this.createComputeds();
        this.setupSubscriptions();
        this.retrieveCustomMessages();
        this.configureAlertMessages();
        this.setPageTitle();
    }

    private retrieveCustomMessages(): void {
        this.customMessages = {
            NextStep: localisationHelper.getString("JoinResources", "NextStep"),
            PaymentButtonText: localisationHelper.getString("JoinResources", "PaymentButtonText"),
            PurchaseError: localisationHelper.getString("JoinResources", "AddMemberError")
        };
    }

    private createComputeds(): void {

        this.showDirectDebit = ko.pureComputed(() => this.JoinConfig.priceDetails.CycleFee() !== null && this.JoinConfig.priceDetails.IsDirectDebit());

        this.isFinalStep = ko.pureComputed(() => {
            return this.JoinConfig.currentStep() === this.JoinConfig.steps.length;
        });

        // buttons
        this.nextButtonText = ko.pureComputed(() => {
            return this.isFinalStep()
                ? this.customMessages.PaymentButtonText
                : `${this.customMessages.NextStep} - ${this.JoinConfig.steps.find(s => s.stepNumber === this.JoinConfig.currentStep() + 1).headerText}`;
        });

        this.CurrentStepPageTitle = ko.pureComputed(() => {
            return `Joining - ${this.JoinConfig.steps.find(s => s.stepNumber === this.JoinConfig.currentStep()).headerText}`;
        });

        this.nextButtonVisible = ko.pureComputed(() => {
            return this.JoinConfig.selectedMembershipPrice() !== null;
        });

        this.nextButtonEnabled = ko.pureComputed(() => {
            if (this.JoinConfig.currentStepModel() && this.JoinConfig.currentStepModel().validationFields) {
                return ValidationService.AreFieldsValid(this.JoinConfig.currentStepModel().validationFields) && !this.addingToBasket();
            }
        });

        this.ariaCurrent = ko.pureComputed(() => this.stepIsActive(this.JoinConfig.currentStep()) ? 'page' : null);

        // visible components
        this.showFacilitySelect = ko.pureComputed(() => {
            return this.stepIsActive(JoiningStepEnum.GymSelection);
        });

        this.showMembershipSelect = ko.pureComputed(() => {
            return this.stepIsActive(JoiningStepEnum.GymSelection) && this.ShowMembershipPane();
        });

        this.showPersonalDetails = ko.pureComputed(() => {
            return this.stepIsActive(JoiningStepEnum.PersonalDetails);
        });

        this.showPayment = ko.pureComputed(() => {
            return this.stepIsActive(JoiningStepEnum.Payment);
        });
    }

    private setupSubscriptions(): void {
        this.JoinConfig.selectedMembershipPrice.subscribe((membershipPrice: IMembershipPriceModel) => {
            if (membershipPrice) {
                this.JoinConfig.currentStep.valueHasMutated();
            }
        });

        this.CurrentStepPageTitle.subscribe(() => {
            this.setPageTitle();
        });

        this.JoinConfig.facilityLoading.subscribe((loading: boolean) => {
            if (!loading && this.showFacilitySelect() && !this.showMembershipSelect()) {
                this.focusFacilitySelection();
            }
        });

        this.showFacilitySelect.subscribe((visible: boolean) => {
            if (visible && !this.showMembershipSelect()) {
                this.focusFacilitySelection();
            }
        });

        this.JoinConfig.membershipsLoading.subscribe((loading: boolean) => {
            if (!loading && this.ShowMembershipPane()) {
                this.focusMembershipSelection();
            }
        });

        this.showPersonalDetails.subscribe((visible: boolean) => {
            if (visible) {
                this.focusFirstControlPersonalDetails();
            }
        });

        this.showPayment.subscribe((visible: boolean) => {
            if (visible) {
                if (this.JoinConfig.priceDetails.IsDirectDebit()) {
                    this.focusAccountNameDD();
                }
                else {
                    this.focusPreviousStepBreadcrumb();
                }
            }
        });
    }

    private setPageTitle(): void {
        document.title = this.CurrentStepPageTitle();
    }

    private getJourneyDetails(journeyId: number): void {
        Deps.JoinService.getJourneyDetails(journeyId).then((journey: IJourneyDetails) => {
            this.JoinConfig.membershipType(journey.MembershipType);
            this.JoinConfig.promoCodeEnabled(journey.EnablePromo);
            this.JoinConfig.enableFlexibleStartDate(journey.EnableFlexibleStartDate);
            this.JoinConfig.maxPeriodForFlexibleStartDate(journey.MaxPeriodForFlexibleStartDate);
            this.JoinConfig.maxPeriodForFlexibleStartDateType(journey.MaxPeriodForFlexibleStartDateType);
            this.JoinConfig.paymentProvider(journey.PaymentProvider);
            this.JoinConfig.setStartDateConfiguration();
            this.JoinConfig.enableReferrer(journey.EnableReferrer);
        });
    }

    private addMembershipToBasket(): void {
        this.addingToBasket(true);
        let joinPromise : Q.Promise<IJoinRequestResponseModel>;
        
        if (this.ContactId && this.ContactId > 0) {
            const rejoinRequest = new JoinMemberRequestModel(this.JoinConfig);
            joinPromise = Deps.JoinService.addNewActiveMembershipToBasket(rejoinRequest);
        } else {
            const request = new JoinMemberRequestModel(this.JoinConfig);
            joinPromise = Deps.JoinService.addMembershipToBasket(request);
        }

        joinPromise
            .then(() => {
                this.handlePaymentRedirect();
            })
            .catch(() => {
                this.ShowErrorMessage(true);
                this.addingToBasket(false);
            });
    }

    private configureAlertMessages(): void {
        this.PurchaseErrorMessage = ko.observableArray([
            {
                Order: 1,
                TextValue: this.customMessages.PurchaseError
            }
        ]);
    }

    private focusFacilitySelection(): void {
        setTimeout(() =>
            $('div.select-two-loading-container select.select2-hidden-accessible').first().focus(),
            200);
    }

    private focusMembershipSelection(): void {
        const membershipSelectionDropdown = this.JoinConfig.facilitiesAssigned
            ? $('div.select-two-loading-container select.select2-hidden-accessible').eq(1)
            : $('div.select-two-loading-container select.select2-hidden-accessible').first();

        setTimeout(() =>
            membershipSelectionDropdown.focus(),
            200);
    }

    private focusFirstControlPersonalDetails(): void {
        setTimeout(() =>
            $('div#personal-details .form-control').filter(':visible').first().focus(),
            200);
    }

    private focusAccountNameDD(): void {
        setTimeout(() =>
            $('div.direct-debit .form-control').filter(':visible').first().focus(),
            200);
    }

    private focusPreviousStepBreadcrumb(): void {
        const currentStep = this.JoinConfig.currentStep();
        setTimeout(() =>
            $('ol.breadcrumb-joining a.breadcrumb-link').filter(`[data-id="${currentStep - 1}"]`).first().focus(),
            200);
    }

    private handlePaymentRedirect(): void {
        if (this.JoinConfig.basketCost() > 0) {
            // redirect to payment
            BasketRoutingService.getBasketPaymentRedirectConfiguration(this.JoinConfig.paymentProvider(), false, null, this.JoinConfig.billingDetails)
                .then((data: any) => {
                    if (data.RedirectUrl !== null) {

                        if (data.RedirectUrl.includes(globalConfig.Urls.Cart.PayCart) || data.RedirectUrl.includes(globalConfig.Urls.Cart.PaymentDetails)) {
                            data.RedirectUrl += '?paymentOnly=true';
                        }

                        Deps.WindowHelper.redirect(data.RedirectUrl);
                    }
                    else if (this.JoinConfig.paymentProvider() === PaymentProviderTypes.WPM) {
                        this.JoinConfig.wpmPaymentRequestXml(data.PaymentRequestXml);
                        this.JoinConfig.wpmPostUrl(data.PostUrl);

                        $("#" + this.JoinConfig.wpmFormId).submit();
                    }
                    else {
                        const form = $('<form method="post">')
                            .attr('action', data.PostUrl)
                            .append($('<input type="hidden" name="res_id">').val(data.MerchantId))
                            .append($('<input type="hidden" name="res_key">').val(data.SecurityKey))
                            .append($('<input type="hidden" name="cc_crypt_type" value="7">'));

                        $("body").append(form);

                        form.submit();
                    }
                })
                .catch(() => {
                    this.ShowErrorMessage(true);
                    this.addingToBasket(false);
                });
        }
        else {
            // membership is free so complete joining
            basketSummaryService.confirmFreeBasket()
                .then(function (response: string) {
                    windowWrapper.redirect(response);
                })
                .catch(() => {
                    this.ShowErrorMessage(true);
                    this.addingToBasket(false);
                });
        }
    }

    stepIsActive(step: number): boolean {
        return step === this.JoinConfig.currentStep();
    }

    stepIsActionable(step: number): boolean {
        if (this.stepIsActive(step)) {
            return false;
        }
        else if (step < this.JoinConfig.currentStep()) {
            return true;                        // can always go back
        }
        else if (step === this.JoinConfig.currentStep() + 1) {
            return this.nextButtonEnabled();    // can only go forward one step
        }
        else {
            return false;
        }
    }

    incrementStep(): void {
        if (!this.nextButtonEnabled()) {
            return;
        }
        if (ValidationService.AreFieldsValid(this.JoinConfig.currentStepModel().validationFields)) {
            if (this.isFinalStep()) {
                if (this.showDirectDebit() && this.JoinConfig.mandateDetails.FeeProcessType() === FeeProcessType.DirectDebit && this.JoinConfig.directDebitConfirmationConfiguration.requiresConfirmation) {
                    //validation DD mandate
                    this.JoinConfig.directDebitConfirmationConfiguration.callback = this.addMembershipToBasket.bind(this);
                    this.JoinConfig.directDebitConfirmationConfiguration.showDirectDebitModalSignal.dispatch();
                    
                } else {
                    this.addMembershipToBasket();    
                }
                                
                return;
            }

            const nextStep = Math.min(this.JoinConfig.currentStep() + 1, this.JoinConfig.steps.length);
            this.goToStep(nextStep);
        }
        window.scrollTo(0, 0);
    }

    goToStep(step: number): void {
        this.JoinConfig.currentStep(step);
    }
}

ko.components.unregister('join-base');
ko.components.register('join-base', {
    viewModel: JoinBase,
    template: { require: 'text!/Themes/Default/Content/ts/Join/join-base.html' }
});